import React from 'react';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { Grid, Form, Button } from 'semantic-ui-react';
import { AppState } from 'services/types';
import { Form as FinalForm, FormRenderProps } from 'react-final-form';

// Actions
import { updateQueryString } from 'services/router/actions';

// Components
import ErrorAlert from 'components/ErrorAlert';
// @ts-ignore

// Services
import { requestCompleteSprayEvents } from 'services/sprayEvents/actions';
import {
    SprayEventDisposal,
    SprayEventUuid,
    SprayEventWeather
} from 'services/sprayEvents/types';

// Form
import SubmittingButton from 'components/SubmittingButton';

import { NAMESPACE, COMPLETE_SPRAY_EVENT_MODAL } from '../../constants';

import './styles.scss';
import {
    selectDisposalIncompleteSprayEventsGroupByStartDate,
    selectWeatherIncompleteSprayEvents,
    selectWeatherIncompleteSprayEventsGroupByStartDate
} from './selectors';
import CompleteCropSprayEventFormTableDisposals from './components/CompleteCropSprayEventFormTableDisposals';
import CompleteCropSprayEventFormTableWeather from './components/CompleteCropSprayEventFormTableWeather';

interface ConnectedProps {
    weatherIncompleteSprayEventsUuids: ReturnType<
        typeof selectWeatherIncompleteSprayEvents
    >;
    weatherIncompleteSprayEvents: ReturnType<
        typeof selectWeatherIncompleteSprayEventsGroupByStartDate
    >;
    disposalIncompleteSprayEvents: ReturnType<
        typeof selectDisposalIncompleteSprayEventsGroupByStartDate
    >;
}

interface DispatchProps {
    updateQueryString: typeof updateQueryString;
    requestCompleteSprayEvents: typeof requestCompleteSprayEvents;
}

export type Props = ConnectedProps & DispatchProps;

export class CompleteCropSprayEventFormComponent extends React.PureComponent<Props> {
    handleSubmit = (items: Record<string, any>): void => {
        const { weatherIncompleteSprayEventsUuids, ...props } = this.props;
        const weatherPayload = weatherIncompleteSprayEventsUuids.reduce(
            (result, groupedUuids) => {
                const weatherUuids = { ...result };

                groupedUuids.uuids.forEach((uuid) => {
                    weatherUuids[uuid] = items[groupedUuids.key];
                });

                return weatherUuids;
            },
            {} as Record<SprayEventUuid, SprayEventWeather>
        );

        const disposalPayload = Object.keys(items).reduce(
            (disposals, field) => {
                // Skip weather fields
                if (!field.includes('_')) {
                    return {
                        ...disposals,
                        [field]: items[field]
                    };
                }

                return disposals;
            },
            {} as Record<SprayEventUuid, SprayEventDisposal>
        );

        const uuids = Object.keys(weatherPayload);

        props.requestCompleteSprayEvents(
            uuids,
            weatherPayload,
            disposalPayload
        );
    };

    render(): React.ReactElement {
        const { weatherIncompleteSprayEvents, disposalIncompleteSprayEvents } =
            this.props;

        return (
            <React.Fragment>
                <ErrorAlert namespace={NAMESPACE} />
                <FinalForm
                    subscription={{ values: true, submitting: true }}
                    onSubmit={this.handleSubmit}
                    render={(props: FormRenderProps): React.ReactElement => {
                        const { handleSubmit } = props;

                        return (
                            <Form
                                onSubmit={handleSubmit}
                                className="complete-spray-event-form || constrain-width"
                            >
                                <React.Fragment>
                                    <Grid className="complete-spray-event-form-section">
                                        <Grid.Row>
                                            <Grid.Column
                                                mobile={16}
                                                tablet={8}
                                                computer={8}
                                            >
                                                <h2 className="complete-spray-event-form-section-title">
                                                    Disposals
                                                </h2>
                                                <p className="complete-spray-event-form-section-subtitle">
                                                    Select disposal type for the
                                                    following orchards
                                                </p>
                                            </Grid.Column>
                                        </Grid.Row>
                                        <CompleteCropSprayEventFormTableDisposals
                                            incompleteSprayEvents={
                                                disposalIncompleteSprayEvents
                                            }
                                        />
                                    </Grid>
                                    <Grid className="complete-spray-event-form-section">
                                        <Grid.Row>
                                            <Grid.Column
                                                mobile={16}
                                                tablet={8}
                                                computer={8}
                                            >
                                                <h2 className="complete-spray-event-form-section-title">
                                                    Weather
                                                </h2>
                                                <p className="complete-spray-event-form-section-subtitle">
                                                    Select weather type for the
                                                    following orchards
                                                </p>
                                            </Grid.Column>
                                        </Grid.Row>
                                        <CompleteCropSprayEventFormTableWeather
                                            incompleteSprayEvents={
                                                weatherIncompleteSprayEvents
                                            }
                                        />
                                    </Grid>
                                    <div className="submit-section">
                                        <SubmittingButton
                                            type="submit"
                                            namespace={NAMESPACE}
                                            primary
                                        >
                                            Submit
                                        </SubmittingButton>
                                    </div>
                                </React.Fragment>
                            </Form>
                        );
                    }}
                />
            </React.Fragment>
        );
    }
}

const mapStateToProps = createStructuredSelector<AppState, {}, ConnectedProps>({
    weatherIncompleteSprayEventsUuids: selectWeatherIncompleteSprayEvents,
    weatherIncompleteSprayEvents:
        selectWeatherIncompleteSprayEventsGroupByStartDate,
    disposalIncompleteSprayEvents:
        selectDisposalIncompleteSprayEventsGroupByStartDate
});

export default connect(mapStateToProps, {
    updateQueryString,
    requestCompleteSprayEvents
})(CompleteCropSprayEventFormComponent);
